import { useState } from 'react';
import { Icons } from '../../ui/Icons';
import { EventRSVPConfig, EventRsvpResponse, UserProfile } from '../../../types';

interface EventRsvpFormProps {
  config: EventRSVPConfig;
  user: UserProfile | null;
  onSubmit: (response: EventRsvpResponse) => void;
  isSubmitting: boolean;
}

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return dateString;
  }
};

export const EventRsvpForm = ({ config, user, onSubmit, isSubmitting }: EventRsvpFormProps) => {
  const [name, setName] = useState(user?.displayName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [guestCount, setGuestCount] = useState(1);
  const [rsvpStatus, setRsvpStatus] = useState<'yes' | 'maybe' | 'no'>('yes');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) newErrors.name = 'Name is required';
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    onSubmit({
      type: 'event_rsvp',
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || undefined,
      guestCount: config.collectGuestCount ? guestCount : undefined,
      rsvpStatus,
      notes: notes.trim() || undefined
    });
  };

  return (
    <div className="space-y-4">
      {/* Event Info Card */}
      <div className="bg-rose-50 rounded-lg overflow-hidden">
        {config.coverImage && (
          <img
            src={config.coverImage}
            alt={config.eventName}
            className="w-full h-40 object-cover"
          />
        )}
        <div className="p-4">
          <h3 className="text-xl font-bold text-gray-800 mb-2">{config.eventName}</h3>

          <div className="space-y-2 text-sm text-gray-600 mb-3">
            <div className="flex items-center gap-2">
              <Icons.Calendar className="w-4 h-4 text-rose-500" />
              <span>{formatDate(config.eventDate)}</span>
              {config.eventTime && <span className="text-gray-400">at {config.eventTime}</span>}
            </div>
            {config.eventLocation && (
              <div className="flex items-start gap-2">
                <Icons.Map className="w-4 h-4 text-rose-500 mt-0.5" />
                <span>{config.eventLocation}</span>
              </div>
            )}
          </div>

          {config.eventDescription && (
            <p className="text-gray-600 text-sm mb-3">{config.eventDescription}</p>
          )}

          {config.mapLink && (
            <a
              href={config.mapLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-rose-600 hover:underline text-sm"
            >
              <Icons.Map className="w-4 h-4" />
              View Location
            </a>
          )}
        </div>
      </div>

      {/* RSVP Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <h4 className="font-medium text-gray-800">RSVP to this event</h4>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Will you be attending?
          </label>
          <div className="flex gap-2">
            {(['yes', 'maybe', 'no'] as const).map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setRsvpStatus(status)}
                className={`flex-1 py-2 px-3 rounded-lg border font-medium transition-colors ${
                  rsvpStatus === status
                    ? status === 'yes'
                      ? 'bg-green-600 text-white border-green-600'
                      : status === 'maybe'
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-gray-500 text-white border-gray-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                }`}
              >
                {status === 'yes' ? "Yes, I'll be there!" : status === 'maybe' ? 'Maybe' : "Can't make it"}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Your Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 text-gray-900 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isSubmitting}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div className="col-span-2 sm:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 text-gray-900 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isSubmitting}
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>

          {config.collectPhone && (
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone (optional)
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 text-gray-900"
                disabled={isSubmitting}
              />
            </div>
          )}

          {config.collectGuestCount && (
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Guests
              </label>
              <select
                value={guestCount}
                onChange={(e) => setGuestCount(Number(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 text-gray-900"
                disabled={isSubmitting}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <option key={num} value={num}>
                    {num} {num === 1 ? 'person' : 'people'}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any dietary restrictions or special requests?"
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 text-gray-900"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3 bg-rose-600 text-white font-bold rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Icons.Loader className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Icons.Calendar className="w-5 h-5" />
              Submit RSVP
            </>
          )}
        </button>
      </form>
    </div>
  );
};
